import {Component} from '@wonderlandengine/api';
import {BooleanName} from '../enums/BooleanName.js';
import {Localization} from '../utils/Localization.js';
import {formatString} from '../ui/utils/utils.js';
import {formatNumber} from '../utils/Utils.js';
import {BooleanStore} from './boolean-store.js';
import {Statistics} from './statistics.js';
import {GameModeType} from '../enums/GameModeType.js';
import {LevelSelect} from './level-select.js';
import {Analytics} from '../classes/Analytics.js';
import {GameFlowController} from './game-flow-controller.js';
import {NotificationLevel} from '../enums/NotificationLevel.js';
import {MenuPanels} from '../models/UIModel.js';
import {NotificationModel, NotificationParams} from '../models/NotificationParams.js';
import {AudioDirector} from './audio-director.js';
import {Sounds} from '../enums/Sounds.js';
import {NotificationPopupModel} from '../models/NotificationModel.js';
import {extra} from '@wonderlandengine/upsdk';
import {ImplementationManager} from '../classes/ImplementationManager.js';
import {MissionCompleteNotificationModel} from '../models/MissionCompleteNotificationModel.js';
import {DataManager} from '../classes/DataManager.js';
import {
    UnlockManagerData,
    UnlockObjectData,
    UnlockParameterData,
    UnlockRewardData,
} from '../types/UnlockManagerTypes.js';
import {CustomAchievements, UnlockTypeEnums} from '../enums/UnlockTypes.js';
import {StoreManager} from './store-manager.js';
import {StoreItemTypes} from '../enums/StoreManagerEnums.js';
import {Area} from '../enums/Area.js';

enum NotificationType {
    Achievement,
    Mission,
}
class PendingNotification {
    type: NotificationType;
    unlock: UnlockObjectData; // used for achievements
    instructions: string; // used for missions
    instructions2: string; // used for missions
}

export enum UnlockNotificationType {
    Unlock,
    Reward,
    RankReward,
}

export class PendingUnlockNotification {
    type: UnlockNotificationType;
    title: string;
    description: string;
    icon: string;
    textureName: string; //TK: This was a texture2D in Unity
    unlock: UnlockObjectData; // used for regular unlocks
}

export class UnlockManager extends Component {
    static TypeName = 'unlock-manager';

    // Singleton
    private static _instance: UnlockManager;

    static get instance(): UnlockManager {
        return UnlockManager._instance;
    }

    get notificationVisible() {
        return this._notificationVisible;
    }

    private _unlockMap: Map<BooleanName, UnlockObjectData> = new Map();
    private _timeToVerifyUnlocks: boolean;
    private _notificationQueue: PendingUnlockNotification[] = [];
    private _achievementQueue: PendingNotification[] = [];
    private _notificationPending = false;
    private _notificationVisible = false;

    private _unlockAchievement: NotificationPopupModel;
    private _unlockManagerData: UnlockManagerData = null;
    private _dataloader: Promise<void>;

    // notification on top of screen
    //public Notification missionCompleteNotification;

    init() {
        if (UnlockManager._instance) {
            console.error('There can only be one instance of UnlockManager Component');
        }
        UnlockManager._instance = this;

        this._dataloader = DataManager.instance
            .loadData<UnlockManagerData>('data/UnlockManager.bin')
            .then((data) => {
                this._unlockManagerData = data;
                for (let i = 0; i < data.unlocks.length; i++) {
                    this._unlockMap.set(data.unlocks[i].unlock, data.unlocks[i]);
                }
            });

        this._timeToVerifyUnlocks = false;
    }

    start() {
        this._unlockAchievement = NotificationPopupModel.instance;
        this._dataloader.then(() => {
            this._verifyUnlocks();
            this._syncAchievements();
        });
    }

    onActivate(): void {
        Statistics.instance.registerConfigChangedCallback(this._configChanged);
        Statistics.instance.registerUpdateCallback(this._notifyStatsChanged);
        Statistics.instance.registerChangedCallback(this._notifyStatsChanged);
    }

    onDeactivate(): void {
        Statistics.instance.unRegisterConfigChangedCallback(this._configChanged);
        Statistics.instance.unRegisterUpdateCallback(this._notifyStatsChanged);
        Statistics.instance.unRegisterChangedCallback(this._notifyStatsChanged);
    }

    update(dt: number) {
        if (this._unlockManagerData == null) {
            return; // Data is not loaded yet.
        }
        if (this._timeToVerifyUnlocks) {
            // && !backend.WaitingForSaveResult)
            this._verifyUnlocks();
            this._timeToVerifyUnlocks = false;
        }

        // Check to see if the first thing in queue is dead yet. If so, remove it and spawn next thing
        if (
            this._achievementQueue.length > 0 &&
            GameFlowController.instance.notificationsAllowed != NotificationLevel.None &&
            this._unlockAchievement.canShow
        ) {
            // && missionCompleteNotification.CanShow)
            let notification = this._achievementQueue.shift();
            if (notification.type == NotificationType.Achievement) {
                this._showAchievement(notification.unlock);
                extra.celebrate();
            } else if (notification.type == NotificationType.Mission) {
                this._showMissionNotification(
                    notification.instructions,
                    notification.instructions2
                );
            }
        }

        if (this._notificationPending && this._canShowPopupNow()) {
            if (this._notificationQueue.length > 0) {
                let unlock = this._notificationQueue.shift();
                this._showNotification(unlock);
            }

            this._notificationPending = false;
        }
    }

    getInstructionsForUnlock(
        lockedObject: BooleanName,
        index: number
    ): {
        instructions: string;
        percentage: number;
        completed: boolean;
    } {
        let instructions = '';
        let completed = false;
        let percentage = 0;

        const unlock = this._unlockMap.get(lockedObject);

        if (unlock) {
            const format = Localization.instance.get(
                unlock.unlockParameters[index].unlockVerbString
            );

            // Show the parameters in string
            if (unlock.unlockParameters[index].hideParametersFromString == false) {
                instructions = formatString(
                    format,
                    formatNumber(unlock.unlockParameters[index].statParameters)
                );
            } // don't show params in string; they aren't applicable
            else {
                instructions = format;
            }

            const statAmount = this._getUnlockProgress(unlock.unlockParameters[index]);

            if (
                statAmount >= unlock.unlockParameters[index].statParameters ||
                BooleanStore.instance.get(lockedObject)
            ) {
                completed = true;
                percentage = 1;
            } else {
                completed = false;
                let statString = formatNumber(statAmount);
                if (statAmount > 0) {
                    percentage = statAmount / unlock.unlockParameters[index].statParameters;
                } else {
                    statString = '0';
                }

                // show x/y if you haven't completed yet
                if (unlock.unlockParameters[index].showProgress == true) {
                    const progressFormat =
                        Localization.instance.get('UnlockProgressFormat');
                    instructions +=
                        ' ' +
                        formatString(
                            progressFormat,
                            statString,
                            formatNumber(unlock.unlockParameters[index].statParameters)
                        );
                }
            }
        }
        return {instructions, percentage, completed};
    }

    /**
     * achievement, fully upgrade
     */
    getHighestUpgradeLevel(upgradeLevel: number): number {
        let powerup = StoreManager.instance.getHighestUpgradeLevel(
            StoreItemTypes.StoreUpgrade
        );
        let perk = StoreManager.instance.getHighestUpgradeLevel(StoreItemTypes.StorePerk);

        return Math.max(powerup, perk);
    }

    gotNumberOfMalwear(goal: number): number {
        let metGoal = StoreManager.instance.getNumberOfMalwear();
        return metGoal;
    }

    gotNumberOfLevelsUnlocked(goal: number): number {
        //island
        let metGoal = 1;

        if (BooleanStore.instance.get(BooleanName.CandylandUnlocked) == true) {
            metGoal++;
        }

        if (BooleanStore.instance.get(BooleanName.ArcticUnlocked) == true) {
            metGoal++;
        }

        if (BooleanStore.instance.get(BooleanName.ForestUnlocked) == true) {
            metGoal++;
        }

        if (BooleanStore.instance.get(BooleanName.DesertUnlocked) == true) {
            metGoal++;
        }

        if (BooleanStore.instance.get(BooleanName.WukAreaUnlocked) == true) {
            metGoal++;
        }

        if (BooleanStore.instance.get(BooleanName.MoonAreaUnlocked) == true) {
            metGoal++;
        }

        if (BooleanStore.instance.get(BooleanName.SwampUnlocked) == true) {
            metGoal++;
        }

        //	Logger.Log("number of levels unlocked = " + metGoal);

        return metGoal;
    }

    getStatHeading(lockedObject: BooleanName) {
        const myString = Localization.instance.get(
            this._unlockMap.get(lockedObject).statString
        );
        return myString;
    }

    getNumberOfRequirements(lockedObject: BooleanName) {
        const length = this._unlockMap.get(lockedObject).unlockParameters.length;
        return length;
    }

    getAchievementWorth(lockedObject: BooleanName) {
        const cost = this._unlockMap.get(lockedObject).unlockParameters[0].achievementWorth;
        return cost;
    }

    getDisableForNow(lockedObject: BooleanName) {
        const showMe = this._unlockMap.get(lockedObject).disableForNow;
        return showMe;
    }

    isGameModeUnlocked(mode: GameModeType): boolean {
        let unlocked = false;

        switch (mode) {
            case GameModeType.Timed:
                unlocked = BooleanStore.instance.get(BooleanName.TimedModeUnlocked);
                break;
            case GameModeType.Survival:
                unlocked = BooleanStore.instance.get(BooleanName.SurvivalModeUnlocked);
                break;
            case GameModeType.Normal:
                unlocked = BooleanStore.instance.get(BooleanName.NormalModeUnlocked);
                break;
            default:
                unlocked = true;
                break;
        }
        return unlocked;
    }

    isEnvironmentUnlocked(area: Area): boolean {
        let unlocked = false;
        switch (area) {
            case Area.Arctic:
                unlocked = BooleanStore.instance.get(BooleanName.ArcticUnlocked);
                break;
            case Area.Desert:
                unlocked = BooleanStore.instance.get(BooleanName.DesertUnlocked);
                break;
            case Area.Forest:
                unlocked = BooleanStore.instance.get(BooleanName.ForestUnlocked);
                break;
            case Area.Island:
                unlocked = true;
                break;
            case Area.Swamp:
                unlocked = BooleanStore.instance.get(BooleanName.SwampUnlocked);
                break;
            case Area.Candy:
                unlocked = BooleanStore.instance.get(BooleanName.CandylandUnlocked);
                break;
            case Area.Moon:
                unlocked = BooleanStore.instance.get(BooleanName.MoonAreaUnlocked);
                break;
            case Area.WukArea:
                unlocked = BooleanStore.instance.get(BooleanName.WukAreaUnlocked);
                break;

            default:
                //Logger.Error('Unknown environment queried for unlock status!');
                break;
        }
        return unlocked;
    }

    payOutReward(reward: UnlockRewardData, rewardType: UnlockNotificationType) {
        if (reward.addCumulatively == true) {
            Statistics.instance.addAnonymousPurchaseTransaction(
                reward.transactionalStat,
                reward.amount
            );
        } else {
            // hack redo look at this
            // only give if you havent gotten it
            let previousValue = Statistics.instance.getTransactional(
                reward.transactionalStat
            );

            if (previousValue < reward.amount) {
                Statistics.instance.addAnonymousPurchaseTransaction(
                    reward.transactionalStat,
                    reward.amount - previousValue
                );
            }
        }

        if (reward.addToUnlockNotifications == true) {
            let unlockNote = new PendingUnlockNotification();
            unlockNote.type = rewardType;
            unlockNote.description = Localization.instance.get(reward.descriptionText);
            unlockNote.title = formatString(
                Localization.instance.get(reward.titleText),
                formatNumber(reward.amount)
            );
            unlockNote.icon = reward.icon;

            //special for rank reward (texture instead of icon string)
            if (unlockNote.type == UnlockNotificationType.RankReward) {
                unlockNote.textureName = reward.textureName;
            }

            if (this._notificationQueue.length > 0 || this._notificationVisible) {
                this._notificationQueue.push(unlockNote);
            } else if (this._canShowPopupNow()) {
                this._showNotification(unlockNote);
            } else {
                this._notificationQueue.push(unlockNote);
                this._notificationPending = true;
            }
        }
    }

    acknowledgedNotification(): boolean {
        if (this._notificationQueue.length > 0) {
            const unlock = this._notificationQueue.shift();

            //remove top, push another one
            if (unlock.type == UnlockNotificationType.RankReward) {
                GameFlowController.instance.popMenu(null);
                this._notificationVisible = false;
            }
            this._showNotification(unlock);
            return true;
        } else {
            GameFlowController.instance.popMenu(() => {
                this._notificationVisible = false;
                GameFlowController.instance.notificationsAllowed =
                    NotificationLevel.Everything;
                GameFlowController.instance.unpause();
            });
            return false;
        }
    }

    // MISSION MANAGER PUTS NOTIFICATION IN QUEUE
    addMissionNotificationToQueue(instructions: string, instructions2: string) {
        const missionNotification = new PendingNotification();
        missionNotification.type = NotificationType.Mission;
        missionNotification.instructions = instructions;
        missionNotification.instructions2 = instructions2;
        // add to list
        this._achievementQueue.push(missionNotification);
    }

    private _getUnlockProgress(parameter: UnlockParameterData): number {
        let statAmount = 0;
        if (parameter.isTransactional) {
            statAmount = Statistics.instance.getTransactional(parameter.transactionalStat);
        } else if (parameter.isUnlockStat) {
            statAmount = 0;
            if (BooleanStore.instance.get(parameter.unlockStat)) {
                statAmount = 1;
            }
        } else if (parameter.isCustomAchievement) {
            // custom achievement
            statAmount = this._verifySpecialUnlocks(
                parameter.customAchievements,
                parameter.statParameters
            );
        } else {
            statAmount = Statistics.instance.getLong(parameter.stat);
        }
        return statAmount;
    }

    private _verifySpecialUnlocks(myCase: CustomAchievements, statParam: number): number {
        let returnValue = 0;

        switch (myCase) {
            case CustomAchievements.GetHighestUpgradeLevel:
                returnValue = this.getHighestUpgradeLevel(statParam);
                break;

            case CustomAchievements.GetNumberMalwear:
                returnValue = this.gotNumberOfMalwear(statParam);
                break;
            case CustomAchievements.GetNumberLevelsUnlocked:
                returnValue = this.gotNumberOfLevelsUnlocked(statParam);
                break;
        }
        return returnValue;
    }

    private _unlockParametersMet(parameters: UnlockParameterData[]): boolean {
        let parametersMet = true;
        // then check all the parameters
        for (let j = 0; j < parameters.length; j++) {
            let statAmount = this._getUnlockProgress(parameters[j]);

            // is the amount had less than what is needed? if so, FAILED
            if (statAmount < parameters[j].statParameters) {
                parametersMet = false;
                break;
            }
        }
        return parametersMet;
    }

    private _unlockParametersMetSingly(parameters: UnlockParameterData[], index: number) {
        let parametersMet = true;
        // then check all the parameters

        let statAmount = this._getUnlockProgress(parameters[index]);

        // is the amount had less than what is needed? if so, FAILED
        if (statAmount < parameters[index].statParameters) {
            parametersMet = false;
        }
        return parametersMet;
    }

    private _showProgress(parameters: UnlockParameterData[]): boolean {
        let parametersMet = false;

        for (let j = 0; j < parameters.length; j++) {
            let statAmount = this._getUnlockProgress(parameters[j]);

            // is the amount had less than what is needed? if so, FAILED
            if (
                Math.ceil(statAmount / 10) == parameters[j].statParameters ||
                Math.ceil(statAmount / 25) == parameters[j].statParameters ||
                Math.ceil(statAmount / 50) == parameters[j].statParameters ||
                Math.ceil(statAmount / 75) == parameters[j].statParameters ||
                Math.ceil(statAmount / 90) == parameters[j].statParameters
            ) {
                parametersMet = false;
                break;
            }
        }

        return parametersMet;
    }

    private _syncAchievement(unlock: UnlockObjectData) {
        if (unlock.unlockParameters[0].achievementWorth > 0) {
            let statAmount = this._getUnlockProgress(unlock.unlockParameters[0]);
            let percentage = 0.0;

            if (
                statAmount >= unlock.unlockParameters[0].statParameters ||
                BooleanStore.instance.get(unlock.unlock)
            ) {
                percentage = 1.0;
            } else {
                if (statAmount > 0) {
                    percentage = statAmount / unlock.unlockParameters[0].statParameters;
                }
            }

            if (percentage > 0.0) {
                //ExternalInterface.Instance.SaveAchievement("" + unlock.unlock, percentage * 100.0f);
                //TODO TK:?? How are we going to do this
            }
        }
    }

    private _syncAchievements() {
        for (let i = 0; i < this._unlockManagerData.unlocks.length; i++) {
            if (
                this._unlockManagerData.unlocks[i].unlockParameters[0].achievementWorth > 0
            ) {
                this._syncAchievement(this._unlockManagerData.unlocks[i]);
            }
        }
    }

    private _verifyUnlocks() {
        // if (backend.WaitingForSaveResult)
        // {
        // 	// don't consider unlocks while we wait for state merge results on the
        // 	// server.
        // 	return;
        // }

        for (let i = 0; i < this._unlockManagerData.unlocks.length; i++) {
            const unlockType: UnlockTypeEnums =
                this._unlockManagerData.unlocks[i].unlockType;

            if (
                unlockType != UnlockTypeEnums.achievement &&
                !this._isUnlockImplemented(this._unlockManagerData.unlocks[i])
            ) {
                continue;
            }
            // if it has not been unlocked yet..
            if (
                BooleanStore.instance.get(this._unlockManagerData.unlocks[i].unlock) ==
                false
            ) {
                let parametersMet = this._unlockParametersMet(
                    this._unlockManagerData.unlocks[i].unlockParameters
                );

                //capability of hiding notification for alternate unlocks (for malwear you buy first.. no need to show pop-up)
                let dontShow = false;
                //////////// ARE THERE ALTERNATE UNLOCKS? (IE you bought it)

                if (
                    this._unlockManagerData.unlocks[i].alternateUnlockParameters.length >
                        0 &&
                    parametersMet == false
                ) {
                    dontShow =
                        this._unlockManagerData.unlocks[i].alternateUnlockParameters[0]
                            .hideNotification;

                    //go through each singly.. if ANY is true, then you passed.
                    //NOTE : this ASSUMES that alternate unlocks are OR
                    for (
                        let j = 0;
                        j <
                        this._unlockManagerData.unlocks[i].alternateUnlockParameters.length;
                        j++
                    ) {
                        parametersMet = this._unlockParametersMetSingly(
                            this._unlockManagerData.unlocks[i].alternateUnlockParameters,
                            j
                        );

                        if (parametersMet == true) {
                            break;
                        }
                    }
                }

                // save progress on partially completed achievements
                if (
                    !parametersMet &&
                    this._unlockManagerData.unlocks[i].disableForNow == false &&
                    this._unlockManagerData.unlocks[i].unlockParameters[0]
                        .achievementWorth > 0
                ) {
                    this._syncAchievement(this._unlockManagerData.unlocks[i]);
                }

                ///////////////////////////////////
                // don't allow locked achievements / unlocks to trigger yet
                if (
                    parametersMet &&
                    this._unlockManagerData.unlocks[i].disableForNow == false
                ) {
                    BooleanStore.instance.set(
                        this._unlockManagerData.unlocks[i].unlock,
                        true
                    );

                    // double check if we don't want to show
                    if (dontShow == false) {
                        dontShow =
                            this._unlockManagerData.unlocks[i].unlockParameters[0]
                                .hideNotification;
                    }

                    // am i an achievement?
                    if (
                        this._unlockManagerData.unlocks[i].unlockParameters[0]
                            .achievementWorth > 0
                    ) {
                        let achievementNotification = new PendingNotification();
                        achievementNotification.type = NotificationType.Achievement;
                        achievementNotification.unlock = this._unlockManagerData.unlocks[i];
                        // add to list
                        this._achievementQueue.push(achievementNotification);

                        // write to external system
                        // ExternalInterface.Instance.SaveAchievement("" + unlocks[i].unlock, 100.0f);
                    } else if (dontShow == false) {
                        // no, i am an unlock
                        let unlockNote = new PendingUnlockNotification();
                        unlockNote.type = UnlockNotificationType.Unlock;
                        unlockNote.unlock = this._unlockManagerData.unlocks[i];

                        if (
                            this._notificationQueue.length > 0 ||
                            this._notificationVisible
                        ) {
                            this._notificationQueue.push(unlockNote);
                        } else if (this._canShowPopupNow()) {
                            this._showNotification(unlockNote);
                        } else {
                            this._notificationQueue.push(unlockNote);
                            this._notificationPending = true;
                        }
                    }

                    // LOG ANALYTICS
                    if (
                        this._unlockManagerData.unlocks[i].unlockParameters[0]
                            .achievementWorth > 0
                    ) {
                        Analytics.LogEventWithValue(
                            'Achievement: ' + this._unlockManagerData.unlocks[i].statString,
                            1
                        );
                    } else {
                        Analytics.LogEventWithValue(
                            'Unlocked: ' + this._unlockManagerData.unlocks[i].statString,
                            1
                        );
                    }

                    // TODO: track this better
                    // Are there any rewards? If so, GIVE EM!!!
                    if (this._unlockManagerData.unlocks[i].unlockRewards.length > 0) {
                        for (
                            let j = 0;
                            j < this._unlockManagerData.unlocks[i].unlockRewards.length;
                            j++
                        ) {
                            this.payOutReward(
                                this._unlockManagerData.unlocks[i].unlockRewards[j],
                                UnlockNotificationType.Reward
                            );
                        }
                    }
                }
                /*
				else if (unlocks[i].showDropdownProgress == true && ShowProgress(unlocks[i].unlockParameters))
				{
					Logger.Log("show progress");
					PendingNotification achievementNotification = new PendingNotification();
					achievementNotification.type = PendingNotification.NotificationType.Achievement;
					achievementNotification.unlock = unlocks[i];
					// add to list
					achievementQueue.Add(achievementNotification);
				}
				*/
            }
        }
    }
    private _isUnlockImplemented(data: UnlockObjectData): boolean {
        return ImplementationManager.instance.isBooleanImplemented(data.unlock);
    }

    private _canShowPopupNow(): boolean {
        const gameFlow = GameFlowController.instance;
        return (
            gameFlow.canChangeMenu &&
            gameFlow.notificationsAllowed == NotificationLevel.Everything &&
            !gameFlow.popupShowing
        );
    }

    private _showNotification(unlock: PendingUnlockNotification) {
        //TK: Due to how Wonderland Engine works different from Unity I had to make some changes
        NotificationModel.instance.resetParams();
        let popUpParams = NotificationModel.instance.params;

        //  if (unlock.type != PendingUnlockNotification.UnlockNotificationType.RankReward)
        //  {
        //  	popUpParams = unlockNotification.GetComponent<NotificationParams>();
        //  }
        //  else
        //  {
        //    popUpParams = rankUpRewardNotification.GetComponent<NotificationParams>();
        //  }

        AudioDirector.instance.playOneShot(Sounds.unlockSFX, true);

        // If you're a normal unlock... (not a rewards to show)
        if (unlock.type == UnlockNotificationType.Unlock) {
            popUpParams.title = Localization.instance.get('UnlockedTitle');
            const unlockStat = unlock.unlock;
            // get title of unlock from stat
            popUpParams.popUpDescription = Localization.instance.get(unlockStat.statString);
            // get text for description
            popUpParams.popUpDescriptionDetail = Localization.instance.get(
                unlockStat.additionalDescriptionText
            );
            // Determine whether to show a "switch now?" message on this screen
            popUpParams.message.functionName2 = 'acknowledgedNotification';
            popUpParams.message.target2 = this;
            if (
                unlockStat.unlockType == UnlockTypeEnums.achievement ||
                unlockStat.unlockType == UnlockTypeEnums.powerup
            ) {
                popUpParams.showSwitchNowObject = false;
                popUpParams.showEquipObject = false;
            } else if (unlockStat.unlockType == UnlockTypeEnums.level) {
                popUpParams.showSwitchNowObject = true;
                popUpParams.showEquipObject = false;
                popUpParams.message.functionName = unlockStat.functionName + 'TryItNow';
                popUpParams.message.target = LevelSelect.instance;
                popUpParams.message.functionName2 = '';
                popUpParams.message.target2 = null;
            } else if (unlockStat.unlockType == UnlockTypeEnums.mode) {
                popUpParams.showSwitchNowObject = true;
                popUpParams.showEquipObject = false;
                popUpParams.message.functionName = unlockStat.functionName + 'TryItNow';
                popUpParams.message.target = GameFlowController.instance;
                popUpParams.message.functionName2 = '';
                popUpParams.message.target2 = null;
            } else if (unlockStat.unlockType == UnlockTypeEnums.malwear) {
                popUpParams.showSwitchNowObject = false;
                popUpParams.showEquipObject = true;
                // MalwearEquipHotkey equipKey = popUpParams.equipObject.GetComponentInChildren<MalwearEquipHotkey>();
                // equipKey.SetUp(unlockStat.unlockRewards[0].malwear);
                popUpParams.message.functionName = '';
                popUpParams.message.target = null;
                popUpParams.message.functionName2 = '';
                popUpParams.message.target2 = null;
            }
            //SHOW CORRECT ICON DEPENDING ON TYPE
            if (
                unlockStat.unlockType == UnlockTypeEnums.level ||
                unlockStat.unlockType == UnlockTypeEnums.mode
            ) {
                // are you level or mode? if so, use this
                popUpParams.iconModeLevel = unlockStat.textureForModeOrLevel;
                popUpParams.iconEnabled = false;
                popUpParams.iconMalwearEnabled = false;
            } else if (unlockStat.unlockType == UnlockTypeEnums.powerup) {
                // you are powerup, use this
                popUpParams.iconSpriteName = unlockStat.icon;
                popUpParams.iconModeLevelEnabled = false;
                popUpParams.iconMalwearEnabled = false;
            } else if (unlockStat.unlockType == UnlockTypeEnums.malwear) {
                popUpParams.iconMalwearEnabled = true;
                popUpParams.iconMalwearSpriteName = unlockStat.icon;
                //popUpParams.iconMalwear.MakePixelPerfect();
                //MalwearEquipHotkey equipKey = popUpParams.equipObject.GetComponentInChildren<MalwearEquipHotkey>();
                //equipKey.SetUp(unlockStat.unlockRewards[0].malwear);
                popUpParams.iconEnabled = false;
                popUpParams.iconModeLevelEnabled = false;
            }
        } else if (
            unlock.type == UnlockNotificationType.Reward ||
            unlock.type == UnlockNotificationType.RankReward
        ) {
            // get title of unlock from stat
            popUpParams.popUpDescription = unlock.title;
            // get text for description
            popUpParams.popUpDescriptionDetail = unlock.description;
            if (unlock.type == UnlockNotificationType.Reward) {
                popUpParams.title = Localization.instance.get('UnlockBonusReward');
                popUpParams.showEquipObject = false;
                popUpParams.showSwitchNowObject = false;
                popUpParams.iconSpriteName = unlock.icon;
                popUpParams.iconMalwearEnabled = false;
                popUpParams.iconModeLevelEnabled = false;

                popUpParams.message.functionName2 = 'acknowledgedNotification';
                popUpParams.message.target2 = this;
            } else if (unlock.type == UnlockNotificationType.RankReward) {
                popUpParams.title = Localization.instance.get('RankUp');
                popUpParams.iconModeLevel = unlock.textureName;
                popUpParams.hideText();
                //popUpParams.iconModeLevel.enabled = true;
                popUpParams.showEquipObject = true;
                popUpParams.showSwitchNowObject = true;
                popUpParams.iconModeLevelEnabled = true;
            }
        }

        if (!this._notificationVisible) {
            if (unlock.type != UnlockNotificationType.RankReward) {
                GameFlowController.instance.pushPopupMenu(MenuPanels.UnlockedSometing);
            } else {
                GameFlowController.instance.pushPopupMenu(MenuPanels.RankedUpReward);
            }
        }
        NotificationModel.instance.updateNotification();
        GameFlowController.instance.notificationsAllowed = NotificationLevel.DropDownOnly;
        this._notificationVisible = true;

        GameFlowController.instance.pause();
    }

    private _configChanged = (newState: Map<string, {}>) => {
        if (newState.has('unlocksEnabled')) {
            const unlocksEnabled = newState['unlocksEnabled'];
            // iterate through unlocks instead of iterating through unlocksEnabled because string -> enum conversion sucks
            // while enum -> string is easy.
            if (unlocksEnabled != null && unlocksEnabled.length > 0) {
                for (let i = 0; i < this._unlockManagerData.unlocks.length; i++) {
                    let key = this._unlockManagerData.unlocks[i].unlock;
                    if (unlocksEnabled.ContainsKey(key)) {
                        this._unlockManagerData.unlocks[i].disableForNow =
                            !unlocksEnabled[key];
                    }
                }
            }
            this._notifyStatsChanged();
        }
    };

    private _showAchievement(unlock: UnlockObjectData) {
        const popUpParams = new NotificationParams();

        if (unlock.icon != '') {
            popUpParams.iconSpriteName = unlock.icon;
        }

        AudioDirector.instance.playOneShot(Sounds.unlockSFX, true);

        // FORMAT TEXT
        //string format = Localization.instance.Get("AchievementUnlockedFormat");

        //make achievement earned in blue
        // Color32 color = new Color32(156,222,255,255);

        // const colorString = string.Format("{0:X2}{1:X2}{2:X2}", color.r, color.g, color.b);

        const stat = Localization.instance.get(unlock.statString);

        // const format = "[" + colorString + "]" + stat + "[-]";
        const format = stat;
        popUpParams.popUpDescription = formatString(
            Localization.instance.get('AchievementUnlockedFormat'),
            format
        );

        this._unlockAchievement.show(popUpParams);
    }

    private _showMissionNotification(instructions: string, instructions2: string) {
        MissionCompleteNotificationModel.instance.show(instructions, instructions2);
        AudioDirector.instance.playOneShot(Sounds.unlockSFX, true);
    }

    private _notifyStatsChanged = () => {
        this._timeToVerifyUnlocks = true;
    };
}
